import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CLIENT_TYPE_LABELS,
  PAYMENT_KIND_LABELS,
  PAYMENT_METHOD_LABELS,
  formatCad,
  lineTotalCents,
  type AppointmentView,
  type InvoiceView,
} from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { formatDateTime } from "../../../core/lib/format";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import {
  getAppointment,
  getInvoice,
  invoiceHtmlUrl,
  invoicePdfUrl,
  sendInvoice,
  updateInvoice,
  voidInvoice,
} from "../lib/api";
import { formatDayShort } from "../lib/dates";
import { GST_LABEL, QST_LABEL } from "../lib/taxes";
import { EmailSendForm, sendOutcomeToast } from "./email-send-form";
import { InvoiceStatusBadge } from "./invoice-status-badge";
import { PaymentModal } from "./payment-modal";

interface InvoiceDetailModalProps {
  invoice: InvoiceView | null;
  onClose: () => void;
  /** Facture rechargée après une action (envoi, encaissement, échéance, annulation). */
  onChange: (invoice: InvoiceView) => void;
}

/** « 2026-11-03 » local, pour un champ date. */
function toDateInput(value: string | number): string {
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Fiche d'une facture du registre : parties, lignes figées, totaux,
 * encaissements, et les actions — imprimer, PDF, envoyer par courriel,
 * encaisser, changer l'échéance, annuler (admin), ouvrir le travail.
 */
export function InvoiceDetailModal({ invoice: v, onClose, onChange }: InvoiceDetailModalProps) {
  const push = useToastStore((s) => s.push);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const [working, setWorking] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingDue, setEditingDue] = useState(false);
  const [dueInput, setDueInput] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState("");
  const [paying, setPaying] = useState<AppointmentView | null>(null);

  useEffect(() => {
    setSending(false);
    setEditingDue(false);
    setVoiding(false);
    setReason("");
    setPaying(null);
    if (v) setDueInput(toDateInput(v.dueAt));
  }, [v?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!v) return null;
  const open = v.status === "en_attente" || v.status === "en_souffrance";

  const reload = async () => onChange(await getInvoice(v.id));

  const send = async (to: string, message: string) => {
    setWorking(true);
    try {
      const result = await sendInvoice(v.id, { to, message: message || null });
      const toast = sendOutcomeToast(result.status, result.to);
      push(toast.level, toast.title, toast.detail);
      onChange(result.invoice);
      setSending(false);
    } catch {
      push("danger", "Envoi impossible");
    } finally {
      setWorking(false);
    }
  };

  const saveDue = async () => {
    if (!dueInput) return;
    setWorking(true);
    try {
      // Fin de la journée choisie, heure locale : une facture reste « en attente »
      // toute la journée de son échéance.
      const at = new Date(`${dueInput}T23:59:59`);
      onChange(await updateInvoice(v.id, { dueAt: at.toISOString() }));
      setEditingDue(false);
      push("success", "Échéance modifiée");
    } catch {
      push("danger", "Modification impossible");
    } finally {
      setWorking(false);
    }
  };

  const confirmVoid = async () => {
    if (!reason.trim()) return;
    setWorking(true);
    try {
      onChange(await voidInvoice(v.id, reason.trim()));
      setVoiding(false);
      push("success", `Facture ${v.number} annulée`, "Le travail peut être refacturé.");
    } catch {
      push("danger", "Annulation impossible");
    } finally {
      setWorking(false);
    }
  };

  const collect = async () => {
    setWorking(true);
    try {
      setPaying(await getAppointment(v.appointmentId));
    } catch {
      push("danger", "Travail introuvable");
    } finally {
      setWorking(false);
    }
  };

  const exempt = v.taxExempt;

  return (
    <Modal open title={`Facture ${v.number}`} onClose={onClose} size="lg">
      <div className="invoice-detail">
        <p className="invoice-detail__eyebrow">
          <InvoiceStatusBadge status={v.status} overdueDays={v.overdueDays} />
          <span>Émise le {formatDayShort(v.issuedAt)}</span>
          {v.status !== "annulee" ? <span>Échéance le {formatDayShort(v.dueAt)}</span> : null}
          {v.sentAt ? (
            <span>
              Envoyée le {formatDateTime(v.sentAt, { dateStyle: "medium" })}
              {v.sentTo ? ` à ${v.sentTo}` : ""}
            </span>
          ) : null}
          {v.voidedAt ? <span>Annulée : {v.voidReason}</span> : null}
        </p>

        <div className="invoice-detail__parties">
          <div>
            <h3 className="invoice-detail__label">Facturé à</h3>
            <p className="invoice-detail__name">
              {v.clientName}
              {v.clientType !== "particulier" ? ` · ${CLIENT_TYPE_LABELS[v.clientType]}` : ""}
            </p>
            {v.clientAddress ? <p>{v.clientAddress}</p> : null}
            {v.clientEmail ? <p>{v.clientEmail}</p> : null}
            {v.clientPhone ? <p>{v.clientPhone}</p> : null}
            {v.vehicleOwnerName ? (
              <p className="invoice-detail__muted">Client du véhicule : {v.vehicleOwnerName}</p>
            ) : null}
          </div>
          <div>
            <h3 className="invoice-detail__label">Travail</h3>
            <p className="invoice-detail__name">{v.vehicleLabel ?? v.appointmentTitle}</p>
            <p>Rendez-vous du {formatDayShort(v.appointmentStartAt)}</p>
            {v.technicianName ? <p>Technicien : {v.technicianName}</p> : null}
            <p>
              <Link to={`/travaux/${v.appointmentId}`} onClick={onClose}>
                Ouvrir le travail
              </Link>
            </p>
          </div>
        </div>

        <table className="table invoice-detail__lines">
          <thead>
            <tr>
              <th>Description</th>
              <th className="travail__num">Qté</th>
              <th className="travail__num">Prix</th>
              <th className="travail__num">Montant</th>
            </tr>
          </thead>
          <tbody>
            {v.lines.length === 0 ? (
              <tr>
                <td colSpan={4}>Aucune ligne.</td>
              </tr>
            ) : (
              v.lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    {l.label}
                    {l.discountPct > 0 ? (
                      <div className="invoice-detail__muted">
                        Rabais {l.discountPct} %{l.discountLabel ? ` — ${l.discountLabel}` : ""}
                      </div>
                    ) : null}
                  </td>
                  <td className="travail__num">{l.quantity}</td>
                  <td className="travail__num">{formatCad(l.priceCents)}</td>
                  <td className="travail__num">{formatCad(lineTotalCents(l))}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <dl className="payments__totals invoice-detail__totals">
          <div className="payments__line">
            <dt>Sous-total</dt>
            <dd>{formatCad(v.subtotalCents)}</dd>
          </div>
          {v.taxes.importedTotalCents !== undefined ? (
            <div className="payments__line">
              <dt>Taxes du devis Urable</dt>
              <dd>{formatCad(v.taxes.importedTotalCents)}</dd>
            </div>
          ) : (
            <>
              <div className={`payments__line${exempt ? " payments__line--muted" : ""}`}>
                <dt>{exempt ? "TPS — exonéré" : GST_LABEL}</dt>
                <dd>{formatCad(v.taxes.gstCents)}</dd>
              </div>
              <div className={`payments__line${exempt ? " payments__line--muted" : ""}`}>
                <dt>{exempt ? "TVQ — exonéré" : QST_LABEL}</dt>
                <dd>{formatCad(v.taxes.qstCents)}</dd>
              </div>
            </>
          )}
          <div className="payments__line payments__line--strong">
            <dt>Total</dt>
            <dd>{formatCad(v.totalWithTaxCents)}</dd>
          </div>
          <div className="payments__line">
            <dt>Payé</dt>
            <dd>{formatCad(v.paidCents)}</dd>
          </div>
          {v.status === "annulee" ? null : (
            <div
              className={`payments__due${v.dueCents === 0 ? " payments__due--settled" : ""}${
                v.status === "en_souffrance" ? " payments__due--late" : ""
              }`}
            >
              <dt>{v.dueCents === 0 ? "Payée" : "Dû"}</dt>
              <dd>{formatCad(v.dueCents)}</dd>
            </div>
          )}
        </dl>

        {v.payments.length > 0 ? (
          <table className="table payments__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Méthode</th>
                <th>Référence</th>
                <th className="travail__num">Montant</th>
              </tr>
            </thead>
            <tbody>
              {v.payments.map((p) => (
                <tr
                  key={p.id}
                  className={p.kind === "remboursement" ? "payments__refund" : undefined}
                >
                  <td className="payments__date">
                    {formatDateTime(p.paidAt, { dateStyle: "medium" })}
                  </td>
                  <td>{PAYMENT_KIND_LABELS[p.kind]}</td>
                  <td>{PAYMENT_METHOD_LABELS[p.method]}</td>
                  <td className="payments__ref">{p.reference ?? "—"}</td>
                  <td className="travail__num">
                    {p.kind === "remboursement" ? "−" : ""}
                    {formatCad(p.amountCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        {v.notes ? <p className="invoice-detail__notes">{v.notes}</p> : null}

        {sending ? (
          <EmailSendForm
            defaultTo={v.clientEmail}
            sending={working}
            what="la facture"
            onSend={(to, message) => void send(to, message)}
            onCancel={() => setSending(false)}
          />
        ) : null}

        {editingDue ? (
          <div className="invoice-detail__inline">
            <Field label="Nouvelle échéance">
              <Input type="date" value={dueInput} onChange={(e) => setDueInput(e.target.value)} />
            </Field>
            <div className="email-send__actions">
              <Button
                variant="ghost"
                size="sm"
                disabled={working}
                onClick={() => setEditingDue(false)}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={working || !dueInput}
                onClick={() => void saveDue()}
              >
                Enregistrer
              </Button>
            </div>
          </div>
        ) : null}

        {voiding ? (
          <div className="invoice-detail__inline invoice-detail__inline--danger">
            <Field
              label="Motif d'annulation"
              hint="La facture garde son numéro et reste au registre comme annulée ; le travail pourra être refacturé."
            >
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Erreur de prix, travail non réalisé…"
                autoFocus
              />
            </Field>
            <div className="email-send__actions">
              <Button
                variant="ghost"
                size="sm"
                disabled={working}
                onClick={() => setVoiding(false)}
              >
                Garder la facture
              </Button>
              <Button
                variant="danger"
                size="sm"
                disabled={working || !reason.trim()}
                onClick={() => void confirmVoid()}
              >
                Annuler la facture
              </Button>
            </div>
          </div>
        ) : null}

        <div className="invoice-detail__actions">
          <Button
            variant="ghost"
            size="sm"
            icon={<Icon name="printer" size={16} />}
            onClick={() => window.open(invoiceHtmlUrl(v.id), "_blank")}
          >
            Imprimer
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={<Icon name="file" size={16} />}
            onClick={() => window.open(invoicePdfUrl(v.id), "_blank")}
          >
            PDF
          </Button>
          {v.status !== "annulee" ? (
            <Button
              variant="ghost"
              size="sm"
              icon={<Icon name="message" size={16} />}
              disabled={working || sending}
              onClick={() => setSending(true)}
            >
              Envoyer par courriel
            </Button>
          ) : null}
          {open ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={working || editingDue}
                onClick={() => setEditingDue(true)}
              >
                Échéance
              </Button>
              <Button variant="primary" size="sm" disabled={working} onClick={() => void collect()}>
                Encaisser
              </Button>
            </>
          ) : null}
          {isAdmin && v.status !== "annulee" ? (
            <Button
              variant="ghost-danger"
              size="sm"
              disabled={working || voiding}
              onClick={() => setVoiding(true)}
            >
              Annuler la facture
            </Button>
          ) : null}
        </div>
      </div>

      {paying ? (
        <PaymentModal
          open
          appointment={paying}
          onClose={() => setPaying(null)}
          onSaved={() => {
            setPaying(null);
            void reload().then(() => push("success", "Paiement enregistré"));
          }}
        />
      ) : null}
    </Modal>
  );
}
